import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { useTheme } from '../contexts/ThemeContext';
import '../App.css';

/**
 * Terms of Service Page
 * This component displays the terms of service and can be accessed directly without authentication.
 * Uses the logged-in user's theme or the system default theme.
 */
function TermsOfService() {
  const { applyTheme } = useTheme();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadTheme = async () => {
      try {
        const token = localStorage.getItem('token');

        if (token) {
          // User is logged in - get their theme preference
          try {
            const response = await api.get('/preferences');
            const userTheme = response.data.find(pref => pref.preference_key === 'theme');
            if (userTheme) {
              applyTheme(userTheme.preference_value);
            }
          } catch (error) {
            console.error('Error loading user theme:', error);
            // Fall back to default theme
            await loadDefaultTheme();
          }
        } else {
          // User not logged in - get system default theme
          await loadDefaultTheme();
        }
      } catch (error) {
        console.error('Error loading theme:', error);
      } finally {
        setLoading(false);
      }
    };

    const loadDefaultTheme = async () => {
      try {
        const response = await api.get('/admin/theme');
        if (response.data.defaultTheme) {
          applyTheme(response.data.defaultTheme);
        }
      } catch (error) {
        console.error('Error loading default theme:', error);
      }
    };

    loadTheme();
  }, [applyTheme]);

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>;
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'var(--color-background)', padding: '2rem' }}>
      <div style={{ maxWidth: '800px', margin: '0 auto', backgroundColor: 'var(--color-bg)', borderRadius: '8px', padding: '2rem', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <h1 style={{ marginBottom: '1.5rem', fontSize: '2rem', color: 'var(--color-primary)' }}>
          Terms of Service
        </h1>

        <div style={{ lineHeight: '1.8', color: 'var(--color-text)' }}>
          <p style={{ marginBottom: '1.5rem', fontSize: '0.875rem', color: 'var(--color-text-light)' }}>
            Last Updated: December 15, 2024
          </p>

          <section style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: 'var(--color-heading)' }}>
              Agreement to Terms
            </h2>
            <p style={{ marginBottom: '1rem' }}>
              Welcome to Brings to Mind. By accessing or using our service, you agree to be bound by these
              Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our service.
            </p>
            <p style={{ marginBottom: '1rem' }}>
              These Terms constitute a legally binding agreement between you and Brings to Mind. Please read
              them carefully.
            </p>
          </section>

          <section style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: 'var(--color-heading)' }}>
              Description of Service
            </h2>
            <p style={{ marginBottom: '1rem' }}>
              Brings to Mind is a personal reflection and journaling platform that allows you to:
            </p>
            <ul style={{ marginLeft: '2rem', marginBottom: '1rem' }}>
              <li style={{ marginBottom: '0.5rem' }}>Create and store personal reflections in text, audio, and video formats</li>
              <li style={{ marginBottom: '0.5rem' }}>Organize reflections by categories and prompts</li>
              <li style={{ marginBottom: '0.5rem' }}>Share reflections with other users (optional)</li>
              <li style={{ marginBottom: '0.5rem' }}>Receive optional reminders to create reflections</li>
              <li style={{ marginBottom: '0.5rem' }}>Customize your experience with themes and preferences</li>
            </ul>
          </section>

          <section style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: 'var(--color-heading)' }}>
              Account Registration and Security
            </h2>
            <p style={{ marginBottom: '1rem' }}>
              To use Brings to Mind, you must create an account. When you register:
            </p>
            <ul style={{ marginLeft: '2rem', marginBottom: '1rem' }}>
              <li style={{ marginBottom: '0.5rem' }}>
                You must provide accurate and complete information
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                You must be at least 13 years of age
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                You are responsible for maintaining the security of your account credentials
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                You are responsible for all activity that occurs under your account
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                You must notify us immediately of any unauthorized access to your account
              </li>
            </ul>
          </section>

          <section style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: 'var(--color-heading)' }}>
              User Content and Ownership
            </h2>
            <p style={{ marginBottom: '1rem' }}>
              <strong>Your Content:</strong> You retain full ownership of all content you create and upload
              to Brings to Mind, including reflections, recordings, and other materials ("User Content").
            </p>
            <p style={{ marginBottom: '1rem' }}>
              <strong>License to Us:</strong> By uploading User Content, you grant us a limited, non-exclusive
              license to store, process, and display your content solely for the purpose of providing our
              service to you.
            </p>
            <p style={{ marginBottom: '1rem' }}>
              <strong>Your Responsibilities:</strong> You are solely responsible for your User Content. You
              represent and warrant that:
            </p>
            <ul style={{ marginLeft: '2rem', marginBottom: '1rem' }}>
              <li style={{ marginBottom: '0.5rem' }}>You own or have the necessary rights to the content</li>
              <li style={{ marginBottom: '0.5rem' }}>Your content does not violate any laws or third-party rights</li>
              <li style={{ marginBottom: '0.5rem' }}>Your content does not contain harmful, offensive, or illegal material</li>
            </ul>
          </section>

          <section style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: 'var(--color-heading)' }}>
              Acceptable Use
            </h2>
            <p style={{ marginBottom: '1rem' }}>
              You agree NOT to use Brings to Mind to:
            </p>
            <ul style={{ marginLeft: '2rem', marginBottom: '1rem' }}>
              <li style={{ marginBottom: '0.5rem' }}>Violate any laws or regulations</li>
              <li style={{ marginBottom: '0.5rem' }}>Infringe on intellectual property rights</li>
              <li style={{ marginBottom: '0.5rem' }}>Upload malicious code, viruses, or harmful software</li>
              <li style={{ marginBottom: '0.5rem' }}>Harass, threaten, or harm other users</li>
              <li style={{ marginBottom: '0.5rem' }}>Impersonate others or misrepresent your identity</li>
              <li style={{ marginBottom: '0.5rem' }}>Attempt to gain unauthorized access to our systems</li>
              <li style={{ marginBottom: '0.5rem' }}>Use automated systems (bots, scrapers) without permission</li>
              <li style={{ marginBottom: '0.5rem' }}>Interfere with or disrupt the service</li>
            </ul>
          </section>

          <section style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: 'var(--color-heading)' }}>
              SMS Communications
            </h2>
            <p style={{ marginBottom: '1rem' }}>
              If you provide a phone number and consent to SMS communications, you agree to receive
              text messages from Brings to Mind as described in our{' '}
              <a
                href="?page=sms-opt-in"
                style={{ color: 'var(--color-primary)', textDecoration: 'underline' }}
              >
                SMS Terms & Conditions
              </a>
              , which are incorporated into these Terms by reference.
            </p>
            <p style={{ marginBottom: '1rem' }}>
              SMS consent is <strong>optional</strong> and not required to use Brings to Mind. You can
              opt out at any time by replying STOP or updating your account settings.
            </p>
          </section>

          <section style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: 'var(--color-heading)' }}>
              Privacy
            </h2>
            <p style={{ marginBottom: '1rem' }}>
              Your use of Brings to Mind is subject to our{' '}
              <a
                href="?page=privacy"
                style={{ color: 'var(--color-primary)', textDecoration: 'underline' }}
              >
                Privacy Policy
              </a>
              , which explains how we collect, use, and protect your personal information.
            </p>
          </section>

          <section style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: 'var(--color-heading)' }}>
              Sharing Features
            </h2>
            <p style={{ marginBottom: '1rem' }}>
              Brings to Mind allows you to optionally share reflections with other users. When you share:
            </p>
            <ul style={{ marginLeft: '2rem', marginBottom: '1rem' }}>
              <li style={{ marginBottom: '0.5rem' }}>
                You control who can view your shared content
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                You can revoke sharing access at any time
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                Shared content may be viewed and potentially saved by recipients
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                You are responsible for what you choose to share
              </li>
            </ul>
          </section>

          <section style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: 'var(--color-heading)' }}>
              Service Availability and Modifications
            </h2>
            <p style={{ marginBottom: '1rem' }}>
              We strive to provide reliable service, but we do not guarantee that:
            </p>
            <ul style={{ marginLeft: '2rem', marginBottom: '1rem' }}>
              <li style={{ marginBottom: '0.5rem' }}>The service will be available at all times without interruption</li>
              <li style={{ marginBottom: '0.5rem' }}>The service will be error-free or free of bugs</li>
              <li style={{ marginBottom: '0.5rem' }}>All features will work on all devices or browsers</li>
            </ul>
            <p style={{ marginBottom: '1rem' }}>
              We reserve the right to modify, suspend, or discontinue any aspect of the service at any
              time, with or without notice.
            </p>
          </section>

          <section style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: 'var(--color-heading)' }}>
              Account Termination
            </h2>
            <p style={{ marginBottom: '1rem' }}>
              <strong>By You:</strong> You may delete your account at any time through account settings.
              Upon deletion, your personal information and content will be permanently removed within 30 days.
            </p>
            <p style={{ marginBottom: '1rem' }}>
              <strong>By Us:</strong> We reserve the right to suspend or terminate your account if you
              violate these Terms, engage in prohibited conduct, or for any other reason at our sole discretion.
            </p>
          </section>

          <section style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: 'var(--color-heading)' }}>
              Disclaimer of Warranties
            </h2>
            <p style={{ marginBottom: '1rem' }}>
              BRINGS TO MIND IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER
              EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS
              FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
            </p>
            <p style={{ marginBottom: '1rem' }}>
              We do not warrant that the service will meet your requirements or that it will be uninterrupted,
              secure, or error-free.
            </p>
          </section>

          <section style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: 'var(--color-heading)' }}>
              Limitation of Liability
            </h2>
            <p style={{ marginBottom: '1rem' }}>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, BRINGS TO MIND SHALL NOT BE LIABLE FOR ANY INDIRECT,
              INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES,
              WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, OR GOODWILL ARISING OUT
              OF YOUR USE OF THE SERVICE.
            </p>
          </section>

          <section style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: 'var(--color-heading)' }}>
              Indemnification
            </h2>
            <p style={{ marginBottom: '1rem' }}>
              You agree to indemnify and hold harmless Brings to Mind from any claims, damages, losses,
              liabilities, and expenses (including legal fees) arising out of:
            </p>
            <ul style={{ marginLeft: '2rem', marginBottom: '1rem' }}>
              <li style={{ marginBottom: '0.5rem' }}>Your use of the service</li>
              <li style={{ marginBottom: '0.5rem' }}>Your User Content</li>
              <li style={{ marginBottom: '0.5rem' }}>Your violation of these Terms</li>
              <li style={{ marginBottom: '0.5rem' }}>Your violation of any third-party rights</li>
            </ul>
          </section>

          <section style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: 'var(--color-heading)' }}>
              Changes to Terms
            </h2>
            <p style={{ marginBottom: '1rem' }}>
              We may update these Terms from time to time. We will notify you of material changes by:
            </p>
            <ul style={{ marginLeft: '2rem', marginBottom: '1rem' }}>
              <li style={{ marginBottom: '0.5rem' }}>Posting the updated Terms on this page</li>
              <li style={{ marginBottom: '0.5rem' }}>Updating the "Last Updated" date</li>
              <li style={{ marginBottom: '0.5rem' }}>Sending you an email notification (for significant changes)</li>
            </ul>
            <p style={{ marginBottom: '1rem' }}>
              Your continued use of the service after changes are posted constitutes acceptance of the
              updated Terms.
            </p>
          </section>

          <section style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: 'var(--color-heading)' }}>
              Governing Law
            </h2>
            <p style={{ marginBottom: '1rem' }}>
              These Terms shall be governed by and construed in accordance with the laws of the jurisdiction
              in which Brings to Mind operates, without regard to conflict of law principles.
            </p>
          </section>

          <section style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: 'var(--color-heading)' }}>
              Contact Information
            </h2>
            <p style={{ marginBottom: '1rem' }}>
              If you have questions about these Terms, please contact us through your account settings
              or at the support email provided in your account dashboard.
            </p>
          </section>

          <div style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid var(--color-border)' }}>
            <p style={{ fontSize: '0.875rem', color: 'var(--color-text-light)' }}>
              Last Updated: December 15, 2024
            </p>
            <p style={{ fontSize: '0.875rem', color: 'var(--color-text-light)', marginTop: '0.5rem' }}>
              Version: 1.0
            </p>
          </div>

          <div style={{ marginTop: '2rem', textAlign: 'center' }}>
            <a
              href="/"
              style={{
                padding: '0.75rem 2rem',
                background: 'var(--color-primary)',
                color: 'white',
                border: 'none',
                borderRadius: 'var(--radius-base, 0.5rem)',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '500',
                textDecoration: 'none',
                display: 'inline-block'
              }}
            >
              Back to Main
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TermsOfService;
